/**
 * VersionHistoryController - Handles version history UI interactions
 * 
 * Manages the Save Version modal, Version History modal, and version CRUD operations.
 * Subscribes to PATCH_SAVED events to refresh the list when modal is open.
 */

import { MODAL_IDS } from '../constants/modals.js';
import { eventBus, EVENTS } from '../services/EventBus.js';
import { showToast } from './ToastController.js';
import { escapeHtml, formatTimestamp } from '../utils/index.js';

// Module state
let storageService = null;
let loadPatchCallback = null;
let showConfirmModalFn = null;

// Track if version history modal is open (for PATCH_SAVED refresh)
let isVersionHistoryModalOpen = false;

/**
 * Initialize the VersionHistoryController
 * @param {Object} deps - Dependencies
 * @param {Object} deps.storageService - Storage service instance
 * @param {Function} deps.loadPatch - Callback to load a patch (for restore)
 * @param {Function} deps.showConfirmModal - Function to show confirmation modal
 * @returns {Object} Public methods for onclick handlers
 */
export function initVersionHistoryController(deps) {
    storageService = deps.storageService;
    loadPatchCallback = deps.loadPatch;
    showConfirmModalFn = deps.showConfirmModal;
    
    // Subscribe to PATCH_SAVED to refresh list if modal is open
    eventBus.on(EVENTS.PATCH_SAVED, () => {
        if (isVersionHistoryModalOpen) {
            renderVersionHistory();
        }
    });
    
    // Return public methods for window/onclick exposure
    return {
        showSaveVersionModal,
        closeSaveVersionModal,
        confirmSaveVersion,
        showVersionHistoryModal,
        closeVersionHistoryModal,
        renderVersionHistory,
        restoreVersionFromHistory,
        deleteVersionFromHistory
    };
}

// ============= SAVE VERSION MODAL =============

/**
 * Show the Save Version modal
 */
function showSaveVersionModal() {
    const modal = document.getElementById(MODAL_IDS.SAVE_VERSION);
    const input = document.getElementById('version-name-input');
    
    // Default name: just datetime
    const defaultName = new Date().toLocaleString();
    input.value = defaultName;
    input.select();
    
    modal.classList.add('active');
}

/**
 * Close the Save Version modal
 */
function closeSaveVersionModal() {
    document.getElementById(MODAL_IDS.SAVE_VERSION).classList.remove('active');
}

/**
 * Confirm and save the version
 */
async function confirmSaveVersion() {
    const input = document.getElementById('version-name-input');
    const name = input.value.trim() || `Version ${Date.now()}`;
    
    const result = await storageService.saveVersion(name);
    
    if (result.success) {
        showToast(`Saved version: "${result.version.name}"`);
    } else {
        showToast('Failed to save version', true);
    }
    
    closeSaveVersionModal();
}

// ============= VERSION HISTORY MODAL =============

/**
 * Show the Version History modal
 */
async function showVersionHistoryModal() {
    const modal = document.getElementById(MODAL_IDS.VERSION_HISTORY);
    isVersionHistoryModalOpen = true;
    await renderVersionHistory();
    modal.classList.add('active');
}

/**
 * Close the Version History modal
 */
function closeVersionHistoryModal() {
    isVersionHistoryModalOpen = false;
    document.getElementById(MODAL_IDS.VERSION_HISTORY).classList.remove('active');
}

/**
 * Render the version history list
 */
async function renderVersionHistory() {
    const list = document.getElementById('version-history-list');
    const versions = await storageService.listVersions();
    
    if (versions.length === 0) {
        list.innerHTML = '<div class="version-history-empty">No saved versions yet.<br>Click "Save Version" to create a checkpoint.</div>';
        return;
    }
    
    list.innerHTML = versions.map(v => `
        <div class="version-history-item" data-version-id="${v.id}">
            <div class="version-history-info">
                <div class="version-history-name">${escapeHtml(v.name)}</div>
                <div class="version-history-meta">
                    <span>${v.deviceCount || 0} devices</span>
                    <span>${v.connectionCount || 0} cables</span>
                    <span>${formatTimestamp(v.timestamp)}</span>
                </div>
            </div>
            <div class="version-history-actions">
                <button onclick="restoreVersionFromHistory('${v.id}')" title="Restore this version">↩️</button>
                <button onclick="deleteVersionFromHistory('${v.id}')" title="Delete" class="danger">🗑️</button>
            </div>
        </div>
    `).join('');
}

/**
 * Restore a version from history
 * @param {string} versionId - The version ID to restore
 */
async function restoreVersionFromHistory(versionId) {
    const confirmed = await showConfirmModalFn({
        title: 'Restore Version',
        message: 'Restore this version? Your current work is auto-saved, so you can use Version History to go back if needed.'
    });
    if (!confirmed) return;
    
    // Save current state as a version before restoring (safety net)
    await storageService.saveVersion(`Before restore - ${new Date().toLocaleString()}`);
    
    // Load the version via the callback (app.js handles DOM updates)
    const success = await loadPatchCallback(versionId);
    
    if (success) {
        closeVersionHistoryModal();
    }
}

/**
 * Delete a version from history
 * @param {string} versionId - The version ID to delete
 */
async function deleteVersionFromHistory(versionId) {
    const confirmed = await showConfirmModalFn({
        title: 'Delete Version',
        message: 'Delete this version? This cannot be undone.',
        confirmText: 'Delete',
        danger: true
    });
    if (!confirmed) return;
    
    const result = await storageService.deleteVersion(versionId);
    
    if (result.success) {
        showToast(`Deleted version: "${result.deletedName}"`);
    }
    
    await renderVersionHistory();
}
